import { Grid, Button } from '@material-ui/core';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import * as React from 'react';
import { useContext, useState } from 'react';
import { AuthContext } from '../_auth/AuthContext';
import { ServicePlan } from '../_types/serviceplan';
import Icon, { SupportedIcons } from '../common/Icon';
import { MigrateMemberships } from '../_service/bulk';
import { ILocationSummary } from '../_types/user';
import { ISearchResult } from '../_types/search';
import { BulkUtilResponse } from '../_types/bulk';
import { PlanByLocationSelector } from '../common/Selector/PlanByLocationSelector';
import { BulkResponseDialog } from '../common/BulkResponseDialog';
import { BulkShowResultsListButton } from '../common/BulkShowResultsListButton';

interface Props {
  plans: ServicePlan[],
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formControl: {
      margin: theme.spacing(1),
      minWidth: 120,
    },
    innerContainer: {
      border: '1px solid',
      borderRadius: 10,
      margin: 15,
      alignItems: 'center',
      textAlign: 'center'
    },
    arrowRow: {
      textAlign: 'center'
    }
    
  })
);

const MigrateMembers: React.FC<Props> = (prop: Props) => {
  const { user } = useContext(AuthContext);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [fromPlan, setFromPlan] = useState<string | null>();
  const [toPlan, setToPlan] = useState<string | null>();
  const [fromLocation, setFromLocation] = useState<ILocationSummary | ISearchResult | null>(user.currentLocation || null);
  const [isMigrating, setIsMigrating] = useState<boolean>(false);
  const [emailList, setEmailList] = useState<string[]>([]);
  const [ignoreList, setIgnoreList] = useState<string[]>([]);
  const [storedEmailList, setStoredEmailList] = useState<string[] | undefined>();
  const [storedIgnoreList, setStoredIgnoreList] = useState<string[] | undefined>();
  const classes = useStyles();


  const _migrate = async () => {
    if (!fromPlan) return alert('Must select a plan to migrate from');
    if (!toPlan) return alert('Must select a plan to migrate to');
    setIsMigrating(true);

    let response: BulkUtilResponse | null = await MigrateMemberships(fromPlan.toString(), toPlan.toString(), Number(fromLocation?.id));

    if (response === null) {
      setIsMigrating(false);
      return alert('Something unexpected went wrong. Check if members were migrated.');
    } 

    if (response !== null) {
      setIsMigrating(false);
      setFromPlan(null);
      setToPlan(null);

      if (response.emails && response.emails.length > 0) {
        setEmailList(response.emails);
        setIsOpen(true)
      }
      
      if (response.ignoreList && response.ignoreList.length > 0) {
        setIgnoreList(response.ignoreList);
        setIsOpen(true)
      }

      if (response.emails && response.emails.length === 0
        && response.ignoreList && response.ignoreList.length === 0) {
          return alert('No memberships were migrated. Make sure you selected the correct plans.')
        }

    }
  }

  React.useEffect(() => {
    setFromPlan(null);
    setToPlan(null);

    if (user.currentLocation) {
      setFromLocation(user.currentLocation);
    }
  }, [user.currentLocation]);

  return (
    <>
            <Grid item>
              <h3>Member Plan-to-Plan Migration</h3>
              <p>First, select a location and a plan whose members you want to move. Second, select a location and a plan you want to move those members into. </p>
            </Grid>
            {/* LOCATION 1 */}
            <PlanByLocationSelector 
              onLocationChange={(newValue: ISearchResult | null) => { 
                setFromLocation(newValue);
              }}
              planInputLabel='From'
              onPlanChange={(event: any) => setFromPlan(event.target.value as string)}
              planValue={fromPlan || ''} 
              plans={prop.plans ?? null} />
          
          <Grid container className={classes.arrowRow}>
        <Grid item xs={4} sm={4}>{<Icon iconName={SupportedIcons.ArrowDownward} color='primary' />}</Grid>
        <Grid item xs={4} sm={4}>{<Icon iconName={SupportedIcons.ArrowDownward} color='primary' />}</Grid>
        <Grid item xs={4} sm={4}>{<Icon iconName={SupportedIcons.ArrowDownward} color='primary' />}</Grid>
          </Grid>

          {/* LOCATION 2 */}
          <PlanByLocationSelector
              planInputLabel='To'
              onPlanChange={(event: any) => setToPlan(event.target.value as string)}
              planValue={toPlan || ''} 
              plans={prop.plans ?? null} />

          {isMigrating ? <p>Migrating, please wait... This can take several minutes...</p> : 
            <div style={{ width: "100%" }}>
              <Grid item xs={12} sm={12} style={{ display: "flex", justifyContent: "flex-end", height: "2.85em", flexDirection: "column" }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => _migrate()}
                  disabled={isMigrating}>
                  Migrate
                </Button>
              </Grid>
              <Grid item xs={12} sm={12} style={{ display: "flex", justifyContent: "flex-start", flexDirection: "column" }}>
                <BulkShowResultsListButton
                  title={"Successfully Migrated " + (storedEmailList?.length || 0).toString() + " Member Emails"}
                  emailList={storedEmailList}
                  ignoreList={storedIgnoreList}
                  hide={storedEmailList === undefined}
                  csvName={"MigrateMembers" + new Date().toLocaleDateString()}
                />
              </Grid>
            </div>
          }
        {(emailList.length > 0 || ignoreList.length > 0) && <BulkResponseDialog
          title="Successfully Migrated Member Emails"
          isOpen={isOpen}
          emailList={emailList}
          ignoreList={ignoreList}
          handleClose={() => {
            setIsOpen(false);
            setStoredEmailList(emailList);
            setStoredIgnoreList(ignoreList);
            setEmailList([]);
            setIgnoreList([]);
          }} />}
    </>
  );
}

export default MigrateMembers;